import { 
    SYNC_ERROR_DIALOG,
    CONNECTION_RESTORED_DIALOG,
    TOKEN_EXPIRED_DIALOG,
    MAX_ATTEMPTS,
    SESSION_DURATION,
    STORAGE_KEY
} from './constants';
import { appCache } from '../../../cache/slices/app/appSlice';
import { SYNC_ERROR_TYPES } from './errorTypes';

/**
 * UI controller for sync errors
 * Manages modal display, retry (refresh) and rate limiting
 */
class SyncErrorHandler {
        /**
         * Public method to get attempts for testing
         */
        getAttempts(errorTypeCode) {
            return this._getAttempts(errorTypeCode);
        }

        /**
         * Public method to reset attempts for testing
         */
        resetAttempts(errorTypeCode) {
            try {
                const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
                delete data[errorTypeCode];
                localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
            } catch {}
        }
    constructor() {
        // Synchronous flag to prevent race condition with multiple simultaneous errors
        this._activeModals = {};
    }

    /**
     * Returns the current attempt count for a given error type from localStorage.
     * If the maximum number of attempts has been reached, checks if the blocking period (SESSION_DURATION)
     * has passed since the last attempt (lastBlocked). If so, resets the counter; otherwise, keeps blocking.
     *
     * @param {string} errorTypeCode - The code of the error type (e.g., 'DEADLOCK', 'TOKEN_EXPIRED').
     * @returns {number} The current attempt count for the given error type.
     */
    _getAttempts(errorTypeCode) {
        try {
            const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
            const record = data[errorTypeCode];

            if (record && record.count >= MAX_ATTEMPTS && record.lastBlocked) {
                if (Date.now() - record.lastBlocked < SESSION_DURATION) {
                    return MAX_ATTEMPTS;
                } else {
                    delete data[errorTypeCode];
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
                    return 0;
                }
            }

            return record?.count || 0;
        } catch {
            return 0;
        }
    }

    /**
     * Increments the attempt counter for a given error type in localStorage.
     * When the maximum number of attempts is reached, sets a blocking timestamp (lastBlocked).
     * This timestamp is used to enforce a cooldown period before attempts can be reset.
     *
     * @param {string} errorTypeCode - The code of the error type (e.g., 'DEADLOCK', 'TOKEN_EXPIRED').
     * @returns {number} The updated attempt count for the given error type.
     */
    _incrementAttempts(errorTypeCode) {
        try {
            const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
            if (data[errorTypeCode]) {
                data[errorTypeCode].count++;
                if (data[errorTypeCode].count === MAX_ATTEMPTS) {
                    data[errorTypeCode].lastBlocked = Date.now();
                }
            } else {
                data[errorTypeCode] = { count: 1 };
            }
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
            return data[errorTypeCode].count;
        } catch (e) {
            console.error('Failed to increment attempts:', e);
            return 0;
        }
    }

    /**
     * Show error modal
     * Checks if any modal is open and controls rate limiting
     */
    showModal(dispatch, error, getState) {
        const errorTypeCode = error.type.code;
        
        // 1. Check attempt limit
        const attempts = this._getAttempts(errorTypeCode);
        if (attempts >= MAX_ATTEMPTS) {
            return;
        }

        // 2. Check if modal of this type is already active (synchronous flag)
        if (this._activeModals[errorTypeCode]) {
            return;
        }

        // 3. Check if any other modal is open in Redux
        const currentDialog = getState?.()?.appCache?.globalAlertDialog;
        if (currentDialog?.dialogId) {
            return;
        }

        // 4. Mark this error type as active (prevents race condition)
        this._activeModals[errorTypeCode] = true;

        // 5. Increment counter ONLY when modal is about to be shown
        this._incrementAttempts(errorTypeCode);

        // 6. Select correct modal based on error type
        let dialogConfig;
        if (errorTypeCode === SYNC_ERROR_TYPES.TOKEN_EXPIRED.code) {
            dialogConfig = TOKEN_EXPIRED_DIALOG;
        } else if (errorTypeCode === SYNC_ERROR_TYPES.PENDING_SYNC.code) {
            dialogConfig = CONNECTION_RESTORED_DIALOG;
        } else {
            // DEADLOCK and others
            dialogConfig = SYNC_ERROR_DIALOG;
        }

        // 7. Show modal
        dispatch(appCache.showGlobalAlertDialog({
            ...dialogConfig,
            handleClose: () => {
                /* eslint-env es2020 */
                globalThis.location.reload();
                // Don't clear flag because reload reloads everything
            }
        }));
    }
}

export const syncErrorHandler = new SyncErrorHandler();
